import { defineStore } from 'pinia'
import Plan, { Stage } from '@/models/plan'
import User from '@/models/user'
import Goal from '@/models/goal'
import Tool from '@/models/tool'
import { useUserStore } from '@/stores/user'
import { useToolStore } from '@/stores/tool'

export const usePlanStore = defineStore('plan', {
  state: () => ({
    // Stores the full plan object.
    plan: undefined,
    // Stores the tool objects associated with the plan.
    tools: undefined,
  }),
  getters: {
    // Returns true if there is an active plan.
    active() {
      return this.plan ? this.plan.active : false
    },
    // Returns the plan type as a Goal text in second person.
    type() {
      return Goal.indexToText(this.plan?.type, false)
    },
    // Returns true if plan type matches user goal.
    typeMatchesGoal() {
      const userStore = useUserStore()
      return this.plan?.type === userStore.user.goal
    },
    // Returns last completed stage.
    lastCompletedStage() {
      const completed = this.plan?.stages.filter(s => {
        return s.state === Stage.STATE_SUCCESS || s.state === Stage.STATE_FAILED
      })
      if (!completed || completed.length == 0) return
      return completed.reduce((max, s) => max.startDate > s.startDate ? max : s)
    },
    // Return currently active stage. If there is no active stage, it means the
    // user has completed a stage but hasn't poceeded to the next stage yet, so
    // check for the most recently completed stage and return that.
    activeStage() {
      const active = this.plan?.stages.find(s => s.state === Stage.STATE_ACTIVE)
      return active ?? this.lastCompletedStage
    },
    // Return currently active stage number.
    activeStageIndex() {
      return this.activeStage?.index
    },
    // Return true if the current stage is success.
    activeStageSuccess() {
      return this.activeStage?.state === Stage.STATE_SUCCESS
    },
    // Return the number of stages in the plan.
    stageCount() {
      if (!this.plan) return
      return Math.max(...this.plan?.stages.map(s => s.index))
    },
    // Return target object of the last stage.
    missionObjective() {
      return this.plan?.stages.find(s => s.index === this.stageCount).target
    },
    // Return true if the plan is completed.
    planComplete() {
      const activeStage = this.activeStage
      return activeStage.index === this.stageCount
        && activeStage.state === Stage.STATE_SUCCESS
    },
    activeTools() {
      return this.tools?.filter(t => !(t.state === Tool.STATE_DOESNT
        || t.state === Tool.STATE_CANT))
    },
    lastUnlockedTool() {
      return this.activeTools?.reduce((max, t) => {
        return max.unlockDate > t.unlockDate ? max : t
      })
    },
  },
  actions: {
    // Explicitly loads the user's plan which is auto-saved to the store.
    async load(fromCache = true) {
      if (!fromCache || !this.plan) {
        const user = await User.getCurrentUser()
        const plan = await user?.getPlan()
        this.tools = await plan?.tools
      }
    },
    reload() {
      return this.load(false)
    },
    async update(plan) {
      if (!(plan instanceof Plan)) {
        throw new Error('Update must be a Plan object.')
      }

      // Updated plans may contain new unlocked tools, so update tool store.
      const toolStore = useToolStore()
      toolStore.reload()
      this.tools = await plan?.tools

      this.plan = plan
    },
  },
})